/**
 * MarketplaceListingForm - Create/edit listing form for sellers
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, getArtifactProjectId } from '../../firebaseInit';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import FormField from '../shared/FormField';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import { MARKETPLACE_CATEGORIES, PRICING_RULES } from '../../contexts/MarketplaceContext';

/**
 * Validate script flow for marketplace listing
 * Returns validation result with isValid flag and any errors
 */
function validateScriptFlow(graph) {
  if (!graph) {
    return { isValid: false, errors: ['No script graph found'] };
  }
  
  // Handle both formats: {nodes: {...}} and flat format
  const nodes = graph.nodes || graph;
  if (!nodes || typeof nodes !== 'object' || Object.keys(nodes).length === 0) {
    return { isValid: false, errors: ['Script has no nodes'] };
  }
  
  // Filter out metadata keys AND auto-generated objection nodes (they are dynamically
  // created from step.objectionResponses and intentionally not part of the main flow graph)
  const nodeIds = Object.keys(nodes).filter(id =>
    id && typeof id === 'string' &&
    id !== 'ui' && id !== 'nodes' &&
    !id.startsWith('objection_obj_') && !id.startsWith('objection_')
  );
  const errors = [];
  
  // Find start nodes — designated opening node always takes priority
  const openingNodes = nodeIds.filter(id => nodes[id]?.isOpeningNode);
  let startNodes = openingNodes.length > 0 ? openingNodes : [];
  
  if (startNodes.length === 0) {
    // Fall back to unreferenced nodes
    const referencedNodes = new Set();
    Object.values(nodes).forEach(node => {
      if (node?.responses) {
        Object.values(node.responses).forEach(resp => {
          const nextId = resp?.nextNode || resp?.nextNodeId || resp?.next;
          if (nextId && nextId !== '__end_script__') {
            referencedNodes.add(nextId);
          }
        });
      }
    });
    startNodes = nodeIds.filter(id => !referencedNodes.has(id));
  }
  
  if (startNodes.length === 0) {
    errors.push('No start node found - designate one node as the opening question');
  } else if (startNodes.length > 1) {
    errors.push(`Multiple start nodes detected (${startNodes.length}) - only one node should be the opening question`);
  }
  
  // Find end nodes
  const endNodes = nodeIds.filter(id => {
    const node = nodes[id];
    if (!node?.responses || Object.keys(node.responses).length === 0) return false;
    return Object.values(node.responses).some(resp => {
      const nextId = resp?.nextNode || resp?.nextNodeId || resp?.next;
      return nextId === '__end_script__' || resp?.isEndNode;
    });
  });
  
  if (endNodes.length === 0) {
    errors.push('No end node found - at least one response should end the script');
  }
  
  // Find incomplete nodes (missing responses or missing next actions)
  const incompleteNodes = nodeIds.filter(id => {
    const node = nodes[id];
    if (!node?.responses || Object.keys(node.responses).length === 0) return true;
    return Object.values(node.responses).some(resp => {
      const nextId = resp?.nextNode || resp?.nextNodeId || resp?.next;
      return !nextId;
    });
  });
  
  if (incompleteNodes.length > 0) {
    errors.push(`Incomplete nodes (${incompleteNodes.length}) - all responses need a next action`);
  }
  
  // Find unreachable nodes (reachability analysis from start)
  if (startNodes.length > 0) {
    const reachable = new Set();
    const toVisit = [...startNodes];
    
    while (toVisit.length > 0) {
      const currentId = toVisit.shift();
      if (reachable.has(currentId) || currentId === '__end_script__' || !currentId) continue;
      reachable.add(currentId);
      
      const node = nodes[currentId];
      if (node?.responses) {
        Object.values(node.responses).forEach(resp => {
          const nextId = resp?.nextNode || resp?.nextNodeId || resp?.next;
          if (nextId && !reachable.has(nextId) && nextId !== '__end_script__') {
            toVisit.push(nextId);
          }
        });
      }
    }
    
    const unreachableNodes = nodeIds.filter(id => !reachable.has(id));
    if (unreachableNodes.length > 0) {
      errors.push(`Unreachable nodes (${unreachableNodes.length}) - some nodes cannot be reached from the start`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    startNodes,
    endNodes,
    incompleteNodes,
    errors,
    nodeCount: nodeIds.length
  };
}

const MarketplaceListingForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editListingId = searchParams.get('edit');
  
  const { user, userTeam, isAdmin, isTeamLead } = useAuth();
  const { templates } = useData();
  const { 
    hasMarketplaceAccess,
    initializeMarketplace,
    createListing, 
    updateListing, 
    getListingById,
    categories 
  } = useMarketplace();

  // Initialize marketplace data on mount
  useEffect(() => {
    initializeMarketplace();
  }, [initializeMarketplace]);

  // Form state
  const [formData, setFormData] = useState({
    templateId: '',
    title: '',
    description: '',
    category: 'other',
    tags: '',
    isFree: true,
    price: 10
  });
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingListing, setLoadingListing] = useState(!!editListingId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [scriptValidation, setScriptValidation] = useState(null);

  // Load existing listing for editing
  useEffect(() => {
    const loadListing = async () => {
      if (!editListingId) return;
      
      try {
        setLoadingListing(true);
        const listing = await getListingById(editListingId);
        
        if (listing) {
          setFormData({
            templateId: listing.templateId,
            title: listing.title,
            description: listing.description,
            category: listing.category,
            tags: listing.tags?.join(', ') || '',
            isFree: listing.isFree,
            price: listing.price || 10
          });
        }
      } catch (err) {
        console.error('Error loading listing:', err);
        setError('Failed to load listing');
      } finally {
        setLoadingListing(false);
      }
    };

    loadListing();
  }, [editListingId, getListingById]);

  // Load template when selected
  useEffect(() => {
    const loadTemplate = async () => {
      if (!formData.templateId) {
        setSelectedTemplate(null);
        setScriptValidation(null);
        return;
      }
      
      setLoading(true);
      try {
        const appId = getArtifactProjectId();
        const templateRef = doc(db, `artifacts/${appId}/teams/${userTeam}/templates`, formData.templateId);
        const templateSnap = await getDoc(templateRef);
        
        if (templateSnap.exists()) {
          const templateData = templateSnap.data();
          // Normalize graph location (could be at data.scriptGraph or graph)
          const graph = templateData?.data?.scriptGraph || templateData?.graph;
          const template = {
            ...templateData,
            graph: graph // Ensure graph is at top level
          };
          setSelectedTemplate(template);
          
          // Validate script flow
          const validation = validateScriptFlow(graph);
          setScriptValidation(validation);
          
          // Auto-fill title if empty
          if (!formData.title && (template.name || templateData?.metadata?.name)) {
            setFormData(prev => ({ ...prev, title: template.name || templateData?.metadata?.name }));
          }
        }
      } catch (err) {
        console.error('Error loading template:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [formData.templateId, userTeam]);

  // Access check
  if (!hasMarketplaceAccess) {
    return (
      <div className="p-4">
        <EmptyState
          icon="🔒"
          title="Access Restricted"
          description="The Script Marketplace is available to Team Leads and Admins only."
          action={
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  // Get available templates for selection
  const availableTemplates = templates ? Object.entries(templates)
    .filter(([id, template]) => template && template.name)
    .map(([id, template]) => {
      // Handle both graph formats: {nodes: {...}} and flat format
      const graph = template.graph;
      const nodes = graph?.nodes || graph;
      const nodeCount = nodes && typeof nodes === 'object' 
        ? Object.keys(nodes).filter(k => k !== 'ui' && k !== 'nodes').length 
        : 0;
      return {
        id,
        name: template.name,
        nodeCount
      };
    }) : [];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.templateId) {
      setError('Please select a template to list');
      return;
    }
    
    if (!formData.title.trim()) {
      setError('Please enter a title');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Please enter a description');
      return;
    }
    
    if (!formData.isFree && (isNaN(Number(formData.price)) || Number(formData.price) < PRICING_RULES.MIN_PRICE)) {
      setError(`Minimum price for paid scripts is $${PRICING_RULES.MIN_PRICE}`);
      return;
    }
    
    // Validate script flow before allowing listing
    if (scriptValidation && !scriptValidation.isValid) {
      setError('Script must have a complete flow before listing. Please fix the issues shown below.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setValidationResult(null);

    try {
      const listingData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        isFree: formData.isFree,
        price: formData.isFree ? 0 : Number(formData.price)
      };

      let result;
      if (editListingId) {
        result = await updateListing(editListingId, listingData);
      } else {
        result = await createListing(formData.templateId, listingData, selectedTemplate);
      }

      if (result.success) {
        // All listings go to pending_review — always show the pending confirmation screen
        setValidationResult({
          status: 'pending_review',
          errors: result.validationErrors
        });
      }
    } catch (err) {
      console.error('Error saving listing:', err);
      setError(err.message || 'Failed to save listing. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingListing) {
    return (
      <div className="p-4 flex justify-center">
        <LoadingSpinner size="large" message="Loading listing..." />
      </div>
    );
  }

  // Show validation result
  if (validationResult) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card padding="md">
          <div className="text-center py-4">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Listing Submitted!
            </h2>
            <p className="text-gray-600 mb-2">
              Your script has been submitted for review. An admin will approve it before it appears in the marketplace.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You'll be able to track the status in your Seller Dashboard.
            </p>
            
            {validationResult.errors && validationResult.errors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left mb-6">
                <p className="font-medium text-yellow-800 mb-2">The admin reviewer will also see these notes:</p>
                <ul className="text-sm text-yellow-700 list-disc list-inside">
                  {validationResult.errors.map((err, i) => (
                    <li key={i}>{err.message}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/marketplace/seller')}
              >
                View My Listings
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/marketplace')}
              >
                Browse Marketplace
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Sub-header */}
      <div className="mb-6">
        <p className="text-gray-600 text-sm">
          Share your script with the TreeGenie community
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Template Selection */}
        {!editListingId && (
          <Card padding="md" className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Select Script to List
            </h3>
            
            {availableTemplates.length === 0 ? (
              <EmptyState
                icon="📋"
                title="No scripts available"
                description="You need to create a script before you can list it on the marketplace."
                action={
                  <Button variant="primary" onClick={() => {
                    window.dispatchEvent(new CustomEvent('navigateTo', { detail: 'myScripts' }));
                  }}>
                    Create a Script
                  </Button>
                }
              />
            ) : (
              <select
                name="templateId"
                value={formData.templateId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a script...</option>
                {availableTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.nodeCount} nodes)
                  </option>
                ))}
              </select>
            )}
            
            {loading && (
              <div className="mt-4 flex items-center gap-2 text-gray-500">
                <LoadingSpinner size="small" inline />
                <span className="text-sm">Loading template details...</span>
              </div>
            )}
            
            {selectedTemplate && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Nodes:</strong> {scriptValidation?.nodeCount || 0}
                </p>
                
                {/* Script Flow Validation Status */}
                {scriptValidation && (
                  <div className={`p-3 rounded-lg ${scriptValidation.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${scriptValidation.isValid ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className={`font-medium text-sm ${scriptValidation.isValid ? 'text-green-700' : 'text-red-700'}`}>
                        {scriptValidation.isValid ? '✅ Script Flow Complete' : '❌ Script Flow Incomplete'}
                      </span>
                    </div>
                    
                    {!scriptValidation.isValid && scriptValidation.errors && (
                      <ul className="text-sm text-red-600 mt-2 space-y-1">
                        {scriptValidation.errors.map((err, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="text-red-400">•</span>
                            <span>{err}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {!scriptValidation.isValid && (
                      <p className="text-xs text-red-500 mt-2">
                        Please fix these issues in the Script Editor before listing on the Marketplace.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Listing Details */}
        <Card padding="md" className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Listing Details
          </h3>
          
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Give your script a catchy title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what makes this script effective. Include the target audience, key features, and results you've achieved..."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 100 characters ({formData.description.length}/100)
              </p>
            </div>

            {/* Industry */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {(categories || MARKETPLACE_CATEGORIES).map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (optional)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="real estate, cold calling, lead gen (comma-separated)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate tags with commas
              </p>
            </div>
          </div>
        </Card>

        {/* Pricing */}
        <Card padding="md" className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Pricing
          </h3>
          
          <div className="space-y-4">
            {/* Free/Paid toggle */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="isFree"
                  checked={formData.isFree}
                  onChange={() => setFormData(prev => ({ ...prev, isFree: true }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Free</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="isFree"
                  checked={!formData.isFree}
                  onChange={() => setFormData(prev => ({ ...prev, isFree: false }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Paid</span>
              </label>
            </div>

            {/* Price input */}
            {!formData.isFree && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (USD)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min={PRICING_RULES.MIN_PRICE}
                    step="1"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum ${PRICING_RULES.MIN_PRICE}. You'll receive {PRICING_RULES.SELLER_COMMISSION * 100}% of each sale.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Your earnings: <strong className="text-green-600">${(formData.price * PRICING_RULES.SELLER_COMMISSION).toFixed(2)}</strong> per sale
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/marketplace')}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || (!editListingId && !formData.templateId) || (scriptValidation && !scriptValidation.isValid)}
          >
            {submitting ? 'Saving...' : editListingId ? 'Update Listing' : 'Create Listing'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MarketplaceListingForm;
